/**
 * smsVerificationClient — the client entry point for phone verification
 * (2026-08-14; the text twin of requestBrandedVerificationEmail).
 *
 * One callable covers first-time add, change of number, and plain resend.
 * Consent state itself is read live from prest0n_sms_consent/{uid} (clients
 * may read their own doc; all mutation is server-side).
 */

import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import db from '../utils/firestore';

export type SmsConsentStatus = 'none' | 'pending' | 'confirmed' | 'opted_out' | 'expired';

export interface SmsConsentState {
  status: SmsConsentStatus;
  /** Masked number ("***1234") — safe for display everywhere. */
  phoneMasked: string | null;
}

export function maskPhone(phone: string | null | undefined): string | null {
  if (!phone) return null;
  if (phone.length <= 4) return '***';
  return `***${phone.slice(-4)}`;
}

/** Live consent state for the signed-in user. Returns an unsubscribe fn. */
export function subscribeSmsConsent(
  uid: string,
  callback: (state: SmsConsentState) => void,
): () => void {
  return onSnapshot(
    doc(db, 'prest0n_sms_consent', uid),
    (snap) => {
      if (!snap.exists()) {
        callback({ status: 'none', phoneMasked: null });
        return;
      }
      const data = snap.data() as any;
      const raw = data?.doubleOptInStatus;
      const status: SmsConsentStatus =
        raw === 'confirmed' || raw === 'opted_out' || raw === 'expired' || raw === 'pending'
          ? raw
          : 'pending';
      callback({ status, phoneMasked: maskPhone(data?.phone) });
    },
    () => callback({ status: 'none', phoneMasked: null }),
  );
}

export interface RequestSmsVerificationResult {
  status: 'sent' | 'already_confirmed' | 'opted_out' | 'no_phone' | 'error';
  phoneMasked?: string;
  /** Ready-to-render lowercase message for non-'sent' outcomes. */
  message?: string;
}

/**
 * Start (or restart) phone verification. Pass a phone to set/replace the
 * number; omit it to resend to the number on file. Never throws.
 */
export async function requestSmsVerification(phone?: string): Promise<RequestSmsVerificationResult> {
  try {
    const fn = httpsCallable<{ phone?: string }, { status: string; phoneMasked?: string }>(
      getFunctions(getApp(), 'us-central1'),
      'requestSmsVerification',
    );
    const res = await fn(phone !== undefined ? { phone } : {});
    const status = (res.data?.status as RequestSmsVerificationResult['status']) || 'error';
    const messages: Record<string, string> = {
      opted_out:
        'this number opted out of texts — reply START from the phone to re-subscribe, then verify again.',
      no_phone: 'add a phone number first.',
      already_confirmed: 'this number is already verified.',
    };
    return { status, phoneMasked: res.data?.phoneMasked, message: messages[status] };
  } catch (e: any) {
    const code = String(e?.code || '');
    return {
      status: 'error',
      message: code.includes('resource-exhausted')
        ? 'too many verification texts — try again in an hour.'
        : code.includes('invalid-argument')
          ? 'that phone number does not look valid.'
          : 'could not send the verification text — please try again.',
    };
  }
}

export default requestSmsVerification;
